import { useSummary } from '@/hooks/useCompany';
import DocumentSchema from '@/components/DocumentSchema';
import { useMemo } from 'react';
import type { Table } from 'schema-dts';
import type {
  OwnershipDataFormatted,
  OwnershipEntity,
} from '../OwnershipChart';
import type { OwnershipValues } from '../OwnershipChart/OwnershipChart';
import { OwnershipDataTable } from './styled';
import { useOwnershipTranslations } from '../../hooks/useOwnershipTranslation';
import { getTranslationKey, stringIsKey } from '../../utils';

interface Props {
  data: Partial<OwnershipEntity>[];
  dataFormatted: OwnershipDataFormatted;
}
const OwnershipTable = ({ data, dataFormatted }: Props) => {
  const summary = useSummary();
  const tickerSymbol = summary.status === 'success' ? summary.tickerSymbol : '';
  const t = useOwnershipTranslations();
  const heading = t('table.schemaHeadline', {
    tickerSymbol,
  });
  const caption = t('table.caption', {
    tickerSymbol,
  });

  const orderedKeys = useMemo(
    () =>
      Object.entries(data[0] || {})
        .sort((a, b) => a[1] - b[1])
        .map((entries) => entries[0]),
    [data]
  );

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: heading,
          about: caption,
        }}
      />
      <OwnershipDataTable
        columnTextAlignments="l,l,l"
        data-cy-id="ownership-breakdown-table"
      >
        <caption data-cy-id="ownership-breakdown-table-caption">
          {caption}
        </caption>
        <thead>
          <tr>
            <th>{t('table.colHead1')}</th>
            <th>{t('table.colHead2')}</th>
            <th>{t('table.colHead3')}</th>
          </tr>
        </thead>
        <tbody>
          {orderedKeys.map((key, i) => {
            const { percent, shares } = dataFormatted[key] as OwnershipValues;
            let label = key;
            if (stringIsKey(key)) {
              label = t(getTranslationKey(key));
            }
            return (
              <tr key={key} data-cy-id={`ownership-breakdown-table-row-${i}`}>
                <td>{label}</td>
                <td>{shares}</td>
                <td>{percent}</td>
              </tr>
            );
          })}
        </tbody>
      </OwnershipDataTable>
    </>
  );
};
export default OwnershipTable;
